import {DI_TOKENS} from '@/di/di.tokens';
import {AnalyticsChangeGiftsTabType} from '@/services/TrackingService/TrackingService.constants';
import {UserGiftCta} from '@/stores/GiftsStore/GiftsStore.types';

import {injected} from 'brandi';

import {HttpError} from '@specials/project-user';

import {ErrorsService} from '@/services/ErrorsService/ErrorsService';
import {LoggerService} from '@/services/LoggerService/LoggerService';
import {PlatformService} from '@/services/PlatformService/PlatformService';
import {IProjectApi} from '@/services/ProjectApi/types';
import {TrackingService} from '@/services/TrackingService/TrackingService';
import {GameStatusService} from '@/services/shared/GameStatusService/GameStatusService';
import {GiftsStore} from '@/stores/GiftsStore/GiftsStore';

export class GiftsService {
  constructor(
    private readonly giftsStore: GiftsStore,
    private readonly projectApi: IProjectApi,
    private readonly loggerService: LoggerService,
    private readonly platformService: PlatformService,
    private readonly trackingService: TrackingService,
    private readonly errorsService: ErrorsService,
    private readonly gameStatusService: GameStatusService,
  ) {}

  public async fetchGifts(): Promise<void> {
    try {
      this.giftsStore.setLoadingFetchStatus();
      this.giftsStore.clearErrorFetchData();

      const response = await this.projectApi.fetchGifts();

      if (response.payload.gifts.length > 0 || response.payload.tickets.length > 0) {
        this.giftsStore.setActiveTabUserGifts();
      }

      this.giftsStore.setGifts(response.payload.gifts);
      this.giftsStore.setTickets(response.payload.tickets);
      this.giftsStore.setHasNewGifts(this.giftsStore.getHasNewGiftsByGiftList());

      this.giftsStore.setSuccessFetchStatus();
    } catch (error) {
      this.loggerService.logHttpError(error as HttpError);

      if (this.errorsService.isErrorRecalculationLeagues(error)) {
        this.gameStatusService.setStatusLeaguesRecalculation();
      } else {
        this.giftsStore.setErrorFetchStatus();
        this.giftsStore.setErrorFetchData(error);
      }
    }
  }

  public seenGifts = async (): Promise<void> => {
    const seenGiftsIds = this.giftsStore.getSeenGiftsIds();

    if (seenGiftsIds.length) {
      try {
        await this.projectApi.seenGifts(seenGiftsIds);
        this.giftsStore.clearSeenGiftsIds();
      } catch (error) {
        this.loggerService.logHttpError(error as HttpError);

        if (this.errorsService.isErrorRecalculationLeagues(error)) {
          this.gameStatusService.setStatusLeaguesRecalculation();
        }
      }
    }
  };

  public onNewGiftViewIntersection(giftId: string): void {
    this.giftsStore.onNewGiftViewIntersection(giftId);
  }

  public setActiveTabGifts = (): void => {
    this.giftsStore.setActiveTabGifts();
    this.trackingService.trackChangeGiftsTab(AnalyticsChangeGiftsTabType.AllGifts);
  };

  public setActiveTabUserGifts = (): void => {
    this.giftsStore.setActiveTabUserGifts();
    this.trackingService.trackChangeGiftsTab(AnalyticsChangeGiftsTabType.UsersGifts);
  };

  public getCtaLink = (cta: UserGiftCta): {url: string; canShowQr: boolean} | null => {
    if (this.platformService.isInvesting() && cta.investAppDeeplink) {
      return {
        url: cta.investAppDeeplink,
        canShowQr: false,
      };
    }

    if (this.platformService.isMb() && cta.deeplink) {
      return {
        url: cta.deeplink,
        canShowQr: false,
      };
    }

    if (this.platformService.detectIsIos() && cta.iosLink) {
      return {
        url: cta.iosLink,
        canShowQr: false,
      };
    }

    if (cta.link) {
      return {
        url: cta.link,
        canShowQr: true,
      };
    }

    return null;
  };

  public clearGiftsStatus = (): void => {
    this.giftsStore.setIdleFetchStatus();
  };

  public clearGiftsErrorData = (): void => {
    this.giftsStore.clearErrorFetchData();
  };

  public initDevPanel = (): void => {};

  public setShowResultsGiftModal = (value: boolean): void => {
    this.giftsStore.setShowResultsGiftModal(value);
  };
}

injected(
  GiftsService,
  DI_TOKENS.giftsStore,
  DI_TOKENS.projectApi,
  DI_TOKENS.loggerService,
  DI_TOKENS.platformService,
  DI_TOKENS.trackingService,
  DI_TOKENS.errorsService,
  DI_TOKENS.gameStatusService,
);
